import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { MapPin, CircleDollarSign, ChevronLeft, ChevronRight } from "lucide-react";

const IMAGE_BASE_URL = "https://beigexmemehouse.s3.amazonaws.com/beige/";

const formatLocation = (location) => {
  if (!location) return "Location not provided";
  try {
    const parsed = typeof location === 'string' ? JSON.parse(location) : location;
    return parsed.address || location;
  } catch {
    return location;
  }
};

const EquipmentDetailsModal = ({ open, onOpenChange, equipment }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Reset image index when modal opens or equipment changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [equipment, open]);

  if (!open || !equipment) return null;

  const displayName =
    equipment.equipment?.equipment_name ||
    equipment.equipment_name ||
    "Unnamed Equipment";

  const manufacturer =
    equipment.equipment?.manufacturer || equipment.manufacturer || "N/A";

  const model = equipment.equipment?.model_number || equipment.model || "N/A";

  // Handle Photos Logic
  const photos = equipment.crew_equipment_photos || [];
  const hasMultipleImages = photos.length > 1;

  const getImageUrl = () => {
    if (photos.length > 0) {
      return `${IMAGE_BASE_URL}${photos[currentImageIndex].file_url}`;
    }
    return "/images/misc/equipment_1.png"; // Fallback
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % photos.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="fixed p-0 flex flex-col overflow-hidden bg-white dark:bg-card border-none z-50
         /* MOBILE STYLES: Bottom Sheet */
         /* Added !top-auto and !translate-y-0 to prevent the button from being pushed down */
         bottom-0 left-0 right-0 w-full !h-[70dvh] rounded-t-[20px]
         !top-auto !translate-y-0 translate-x-0
         data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom

         /* DESKTOP STYLES: Right Drawer */
         md:top-0 md:right-0 md:left-auto md:!h-full md:w-[450px] md:rounded-none md:max-w-none
         md:!translate-x-0 md:!translate-y-0
         md:data-[state=open]:slide-in-from-right md:data-[state=open]:slide-in-from-bottom-0"
      >
        {/* ================= HEADER ================= */}
        <DialogHeader className="px-4 py-4 border-b dark:border-border shrink-0">
          <DialogTitle className="text-lg font-semibold dark:text-foreground">
            Equipment Details
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 pb-4 md:pb-6">
          {/* Image Carousel Section */}
          <div className="relative mt-4 rounded-xl overflow-hidden border dark:border-border bg-gray-50 dark:bg-secondary/10 group">
            <img
              src={getImageUrl()}
              alt={displayName}
              onError={(e) => { e.target.src = "/images/misc/equipment_1.png"; }}
              className="w-full h-[280px] object-contain p-2 transition-all duration-300"
            />

            {/* Navigation Buttons - Only show if multiple images */}
            {hasMultipleImages && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black text-white p-1.5 rounded-full transition-opacity opacity-0 group-hover:opacity-100"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black text-white p-1.5 rounded-full transition-opacity opacity-0 group-hover:opacity-100"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>

                {/* Counter Badge */}
                <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-2 py-1 rounded-md font-medium">
                  {currentImageIndex + 1} / {photos.length}
                </div>
              </>
            )}
          </div>

          {/* Title Area */}
          <div className="mt-4 space-y-2">
            <h2 className="text-xl font-bold text-gray-900 dark:text-foreground">{displayName}</h2>

            <span className="flex items-center gap-1 text-sm text-gray-500 dark:text-muted-foreground">
              <MapPin className="w-4 h-4" />
              {formatLocation(equipment.storage_location)}
            </span>
          </div>

          {/* Technical Details */}
          <section className="mt-4 md:mt-6 border dark:border-border rounded-xl p-4 shadow-sm dark:bg-secondary/5">
            <h3 className="font-semibold text-indigo-600 dark:text-indigo-400 mb-4">
              Technical Details
            </h3>

            <div className="grid grid-cols-2 gap-y-4 text-sm">
              <div>
                <p className="text-xs text-gray-400 dark:text-muted-foreground font-semibold uppercase">
                  Manufacturer
                </p>
                <p className="font-medium dark:text-foreground">{manufacturer}</p>
              </div>

              <div>
                <p className="text-xs text-gray-400 dark:text-muted-foreground font-semibold uppercase">
                  Model
                </p>
                <p className="font-medium dark:text-foreground">{model}</p>
              </div>

              <div>
                <p className="text-xs text-gray-400 dark:text-muted-foreground font-semibold uppercase">
                  Serial Number
                </p>
                <p className="font-medium dark:text-foreground">
                  {equipment.serial_number || "N/A"}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-400 dark:text-muted-foreground font-semibold uppercase">
                  Market Price
                </p>
                <p className="font-medium dark:text-foreground">
                  {equipment.market_price ? `$${equipment.market_price}` : "N/A"}
                </p>
              </div>
            </div>
          </section>

          {/* Description */}
          <section className="mt-4 border dark:border-border rounded-xl p-4 shadow-sm dark:bg-secondary/5">
            <h3 className="font-bold mb-2 dark:text-foreground">Description</h3>
            <p className="text-sm text-gray-600 dark:text-muted-foreground leading-relaxed">
              {equipment.description || "No description available."}
            </p>

            {equipment.condition_notes && (
              <div className="mt-4">
                <h4 className="text-xs font-bold text-gray-400 dark:text-muted-foreground uppercase">
                  Condition Notes
                </h4>
                <p className="text-sm text-gray-600 dark:text-muted-foreground">
                  {equipment.condition_notes}
                </p>
              </div>
            )}
          </section>
        </div>

        {/* ================= FOOTER (STICKY) ================= */}
        <div className="shrink-0 border-t dark:border-border px-4 py-4 bg-white dark:bg-card flex items-center justify-between">
          <div>
            {/* <span className="text-[10px] uppercase text-gray-400 dark:text-muted-foreground font-bold">
              Rental Price
            </span> */}
            <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
              <CircleDollarSign className="w-4 h-4 md:w-5 md:h-5" />
              <span className="text-sm md:text-lg font-bold">
                ${equipment.rental_price || "0.00"}
              </span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EquipmentDetailsModal;
